import React, { useEffect, useState } from "react";

import dayjs from "dayjs";
import customParse from "dayjs/plugin/customParseFormat";

import errorMessages from "../../../../utils/validation/errorMessages";
import Validator from "../../../../utils/validation/Validator";
import Icon from "../../Icon";
import FormError from "../FormError";
import * as styles from "./style.module.scss";

dayjs.extend(customParse);

const validator = new Validator();

const Input = ({
    className = "",
    wrapperClassName = "",
    value,
    onChange,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBlur = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onKeyUp = () => {},
    placeholder,
    name,
    withoutWrapper = false,
    validation = null,
    fullWidth = false,
    errorClassName = "",
    type = "text",
    id = null,
    small = false,
    textCenter = false,
    bold = false,
    isDisabled = false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onError = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onErrorSolved = () => {},
    forceError = false,
    required = false,
    icon = false,
}) => {
    const [hasError, setHasError] = useState(false);
    const [validateOnKeyUp, setValidateOnKeyUp] = useState(false);

    useEffect(() => {
        if (forceError === true) {
            setHasError(true);
        }
    }, [forceError]);

    /**
     * Get the corresponding validator.
     */
    const isValid = validator.getValidator(validation, value, required);

    /**
     * If we have a value while we focus the field, start validation on key up.
     */
    const onFocus = () => {
        if (value && value.length > 0) {
            setValidateOnKeyUp(true);
        }
    };

    /**
     * If we should validate on key up or we have an error, validate.
     */
    const localOnKeyUp = () => {
        onKeyUp();

        if ((validateOnKeyUp || hasError) && validation !== null) {
            if (isValid()) {
                setHasError(false);
                onErrorSolved();
            } else {
                setHasError(true);
                onError();
            }
        }
    };

    /**
     * Returns the error message for the validation type.
     */
    const getErrorMessage = () => {
        // Strip a prefix, if we have one.
        const parts = validation.split(":");
        const key = parts[parts.length - 1];
        return errorMessages[key];
    };

    /**
     * When we leave the input.
     */
    const lokalOnBlur = () => {
        if (validation !== null) {
            if (isValid()) {
                setHasError(false);
                onErrorSolved();
            } else {
                setHasError(true);
                onError();
            }
        }
        onBlur();
    };

    /**
     * Returns the input based on the given type.
     */
    const getInput = () => {
        if (type === "textarea") {
            return (
                <textarea
                    id={id === null ? name : id}
                    className={`textarea ${className} ${
                        small ? styles.small : ""
                    } ${fullWidth && !withoutWrapper ? "is-full-width" : ""} ${
                        bold ? styles.bold : ""
                    } ${textCenter ? styles.textCenter : ""} ${
                        isDisabled ? styles.isDisabled : ""
                    }`}
                    onFocus={onFocus}
                    placeholder={placeholder}
                    name={name}
                    onKeyUp={localOnKeyUp}
                    onChange={onChange}
                    value={value}
                    onBlur={lokalOnBlur}
                    required={required}
                ></textarea>
            );
        }

        return (
            <input
                id={id === null ? name : id}
                className={`input ${className} ${small ? styles.small : ""} ${
                    fullWidth && !withoutWrapper ? "is-full-width" : ""
                } ${bold ? styles.bold : ""} ${
                    textCenter ? styles.textCenter : ""
                } ${isDisabled ? styles.isDisabled : ""}`}
                onFocus={onFocus}
                type={type}
                placeholder={placeholder}
                name={name}
                onKeyUp={localOnKeyUp}
                onChange={onChange}
                value={value}
                onBlur={lokalOnBlur}
                autoComplete={
                    name == "zipcode" ||
                    name == "consumption" ||
                    name == "street" ||
                    name == "houseNumber" ||
                    name == "streetSelectList" ||
                    name == "placeSelectList" ||
                    name == "houseNumber"
                        ? "off"
                        : "on"
                }
            />
        );
    };

    if (withoutWrapper) {
        return getInput();
    }

    return (
        <div
            className={`field ${
                fullWidth ? "is-full-width" : ""
            } ${wrapperClassName}`}
        >
            <div
                className={`control ${
                    hasError || icon !== false ? "has-icons-right" : ""
                }`}
            >
                {getInput()}
                {hasError && (
                    <span
                        className={`icon is-small is-right ${styles.errorIcon} ${styles.icon}`}
                    >
                        !
                    </span>
                )}
                {icon !== false && (
                    <span className={`icon is-small is-right ${styles.icon}`}>
                        <Icon type={icon} tiny />
                    </span>
                )}
            </div>

            {hasError && (
                <FormError
                    className={errorClassName}
                    gtag={`error:${name}`}
                    message={getErrorMessage()}
                />
            )}
        </div>
    );
};

export default Input;
